import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Skeleton } from "primereact/skeleton";
import { useNavigate } from "react-router-dom";
import FileIcon from "~/components/file-type-icon";
import { downloadFileHandler, openPreviewInNewTab } from "~/utils/fileUtils";

const getFilenameFromReference = (fileReference) => {
  if (!fileReference) return "";
  try {
    const url = new URL(fileReference);
    const pathname = url.pathname;
    const filename = pathname.substring(pathname.lastIndexOf("/") + 1);
    return decodeURIComponent(filename);
  } catch (error) {
    console.error("Error extracting filename from reference:", error);
    return "";
  }
};

const PayslipTable = ({ loading, data, processId, handleConfirm, isOwner }) => {
  const navigate = useNavigate();
  const skeletonRows = [...Array(5)];

  const navigateHandler = (rowData) => {
    navigate(
      `/process/${processId}/conversations/internal?commentId=${rowData?.payslipMessageData?.id}&commentType=PAYSLIP`,
    );
  };

  const downloadFile = (rowData) => {
    downloadFileHandler({
      blob_url: rowData?.file_reference,
      name: rowData?.extracted_data?.original_filename,
    });
  };

  const deleteFileHanlder = (rowData) => {
    if (isOwner) {
      handleConfirm(rowData);
    }
  };

  const previewHandler = (rowData) => {
    openPreviewInNewTab({
      objectURL: rowData?.file_reference,
    });
  };

  return (
    <DataTable
      value={loading ? skeletonRows : data}
      className="datatable-right-align"
      emptyMessage="No result found"
    >
      <Column
        header="Name"
        body={(rowData) => {
          if (loading) {
            return <Skeleton />;
          }
          return (
            <div className="flex align-items-center">
              <FileIcon
                filetype={"application/pdf"}
                filename={""}
                downloadFile={() => {}}
              />
              <span
                className="link-item underline cursor-pointer"
                onClick={() => previewHandler(rowData)}
              >
                {getFilenameFromReference(rowData?.file_reference)}
              </span>
            </div>
          );
        }}
      />
      <Column
        header="Category"
        body={() => (loading ? <Skeleton /> : "Payslips")}
      />
      <Column
        header="Status"
        body={(rowData) => {
          if (loading) {
            return <Skeleton />;
          }

          // "" or "pending" → Processing (with spinner)
          if (
            rowData?.match_status === "pending" ||
            rowData?.match_status === ""
          ) {
            return (
              <div className="flex align-items-center gap-2">
                <span className="pi pi-spin pi-spinner status__processing" />
                Processing
              </div>
            );
          }

          // Statuses that need employee mapping (Awaiting Review - needs employee mapping)
          if (
            rowData?.match_status === "unmatched" ||
            rowData?.match_status === "extraction_failed" ||
            rowData?.match_status === "no_match" ||
            rowData?.match_status === "invalidated"
          ) {
            return (
              <div
                className="awaiting__review"
                onClick={() => navigateHandler(rowData)}
              >
                Awaiting Review <i className="pi pi-arrow-right" />
              </div>
            );
          }

          // Statuses ready for approval (Awaiting Review - ready for approval)
          if (
            rowData?.match_status === "matched" ||
            rowData?.match_status === "multiple_matches" ||
            rowData?.match_status === "reprocessed" ||
            rowData?.match_status === "source"
          ) {
            return (
              <div
                className="awaiting__review"
                onClick={() => navigateHandler(rowData)}
              >
                Awaiting Review <i className="pi pi-arrow-right" />
              </div>
            );
          }

          // Statuses that are submitted (Submitted - with check icon)
          if (
            rowData?.match_status === "approved" ||
            rowData?.match_status === "manually_corrected"
          ) {
            return (
              <div>
                <i className="pi pi-check-circle icon-submit" /> Submitted
              </div>
            );
          }

          // Source status - show nothing
          if (rowData?.match_status === "source") {
            return null;
          }

          return rowData?.match_status || "-";
        }}
      />
      <Column
        body={(rowData) =>
          loading ? (
            <Skeleton />
          ) : (
            <div className="flex gap-2">
              <i
                className="pi pi-download cursor-pointer mr-2"
                onClick={() => downloadFile(rowData)}
              />
              {isOwner && (
                <i
                  className="pi pi-trash pencil-pointer"
                  onClick={() => deleteFileHanlder(rowData)}
                />
              )}
            </div>
          )
        }
      />
    </DataTable>
  );
};

export default PayslipTable;
